import React from 'react';
import { VIDEO_PRESETS, applyVideoPreset, PRESET_CUSTOM } from '../../lib/videoPresets';

/**
 * VideoPlatformPicker
 * -------------------
 * A 6-tile grid letting users pick "where will this video run?". The chosen
 * preset auto-fills aspect ratio, duration, model, and render-text. Custom
 * leaves everything as-is so power-users still get the manual controls below.
 *
 * Props
 *  - selected: current preset key (string | null)
 *  - onApply: (newFormData, preset) => void  — caller merges into its form state
 *  - formData: current form state (used by applyVideoPreset)
 *  - dense:   compact layout (true) for narrower forms
 *  - recommendedKey: preset key suggested from another field (e.g. primary_platform)
 *  - recommendedSourceLabel: e.g. "Instagram" — used in the suggestion banner
 */
export const VideoPlatformPicker = ({
  selected,
  onApply,
  formData,
  dense = false,
  recommendedKey = null,
  recommendedSourceLabel = null,
}) => {
  const handlePick = (preset) => {
    const patched = applyVideoPreset(formData || {}, preset.key);
    onApply?.(patched, preset);
  };

  const gridClass = dense
    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5'
    : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3';

  const recommendedPreset = recommendedKey
    ? VIDEO_PRESETS.find((p) => p.key === recommendedKey)
    : null;
  const showRecommendBanner = recommendedPreset && !selected;

  return (
    <div
      className="p-4 rounded-xl border border-slate-200 dark:border-slate-700/60 bg-gradient-to-br from-purple-50/60 via-white to-blue-50/60 dark:from-purple-900/10 dark:via-slate-900/30 dark:to-blue-900/10"
      data-testid="video-platform-picker"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h5 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
            🎯 Where will this video run?
          </h5>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            Pick a platform — we&apos;ll auto-tune aspect ratio, duration, model &amp; text rendering. Tweak below if needed.
          </p>
        </div>
      </div>

      {showRecommendBanner && (
        <button
          type="button"
          onClick={() => handlePick(recommendedPreset)}
          data-testid="video-platform-picker-suggestion-banner"
          className="w-full mb-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base leading-none" aria-hidden="true">💡</span>
            <span className="text-[12px] text-amber-900 dark:text-amber-200 truncate">
              {recommendedSourceLabel ? (
                <>Suggested for <span className="font-semibold">{recommendedSourceLabel}</span>: {recommendedPreset.emoji} <span className="font-semibold">{recommendedPreset.label}</span></>
              ) : (
                <>Suggested: {recommendedPreset.emoji} <span className="font-semibold">{recommendedPreset.label}</span></>
              )}
            </span>
          </div>
          <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300 whitespace-nowrap">Apply →</span>
        </button>
      )}

      <div className={gridClass}>
        {VIDEO_PRESETS.map((preset) => {
          const isSelected = selected === preset.key;
          const isRecommended = !selected && recommendedKey === preset.key;
          const isCustom = preset.key === PRESET_CUSTOM;
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => handlePick(preset)}
              data-testid={`video-preset-${preset.key}`}
              data-selected={isSelected ? 'true' : undefined}
              data-recommended={isRecommended ? 'true' : undefined}
              className={`relative text-left p-3 rounded-xl border-2 transition-all duration-150 group ${
                isSelected
                  ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30 shadow-md ring-2 ring-purple-200 dark:ring-purple-800/50'
                  : isRecommended
                  ? 'border-amber-400 dark:border-amber-500 bg-amber-50/60 dark:bg-amber-900/20 hover:border-amber-500 dark:hover:border-amber-400 hover:shadow-sm'
                  : 'border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/40 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between mb-1.5">
                <span className="text-2xl leading-none" aria-hidden="true">{preset.emoji}</span>
                <span
                  className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    isCustom
                      ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      : preset.model === 'gemini_omni_video'
                      ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                      : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  }`}
                >
                  {preset.aspect_ratio}
                </span>
              </div>
              <div className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">
                {preset.label}
              </div>
              {!isCustom && (
                <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                  <span>{preset.duration}s</span>
                  <span aria-hidden="true">·</span>
                  <span className="font-medium">{preset.badge}</span>
                  {preset.render_text && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-medium">Text ✓</span>
                    </>
                  )}
                </div>
              )}
              {isCustom && (
                <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                  Choose manually
                </div>
              )}
              {isSelected && (
                <div className="absolute top-1.5 right-1.5">
                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              {isRecommended && !isSelected && (
                <div className="absolute -top-2 left-2 px-1.5 py-0.5 rounded bg-amber-400 dark:bg-amber-500 text-[8px] font-bold uppercase tracking-wider text-amber-950 shadow-sm">
                  Suggested
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selected && selected !== PRESET_CUSTOM && (
        <div
          className="mt-3 text-[11px] text-slate-600 dark:text-slate-400 bg-white/60 dark:bg-slate-800/40 rounded-lg p-2 border border-slate-100 dark:border-slate-700/40"
          data-testid="video-platform-picker-summary"
        >
          {(() => {
            const p = VIDEO_PRESETS.find((x) => x.key === selected);
            if (!p) return null;
            return (
              <>
                <span className="font-medium text-slate-900 dark:text-white">{p.label}:</span>{' '}
                {p.description}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default VideoPlatformPicker;
